import { useCallback, useMemo, useState, useEffect } from 'react'
import { IonIcon } from '../../../Icons/IonIcon'
import { Switch, SwitchSize } from '../../../components/legacy/Form/Switch/Switch'
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '../../../lib'

const tabDefs = [
  { id: 'tasks', label: 'Tasks' },
  { id: 'activities', label: 'Activities' },
  { id: 'comments', label: 'Comments' },
  { id: 'forms', label: 'Forms' },
]

type TabId = 'tasks' | 'activities' | 'comments' | 'forms'

interface DraggableTabRowProps {
  label: string
  enabled: boolean
  onToggle: () => void
  listeners: any
  attributes: any
  isDragging: boolean
  setNodeRef: (el: HTMLElement | null) => void
}

const DraggableTabRow = ({
  label,
  enabled,
  onToggle,
  listeners,
  attributes,
  isDragging,
  setNodeRef,
}: DraggableTabRowProps) => (
  <div
    ref={setNodeRef}
    {...attributes}
    className={cn('flex items-center gap-2 p-2 justify-between', isDragging && 'opacity-50')}
  >
    <div className='flex items-center gap-2'>
      <span {...listeners} className='cursor-grab flex items-center'>
        <IonIcon name='c_draggable' className='text-grey-s3' />
      </span>
      <span className='text-sm font-medium text-grey-s6'>{label}</span>
    </div>

    <Switch size={SwitchSize.SMALL} checked={enabled} onChange={onToggle} />
  </div>
)

interface SortableTabRowProps {
  id: TabId
  label: string
  enabled: boolean
  onToggle: () => void
}

const SortableTabRow = ({ id, label, enabled, onToggle }: SortableTabRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <div style={style}>
      <DraggableTabRow
        label={label}
        enabled={enabled}
        onToggle={onToggle}
        listeners={listeners}
        attributes={attributes}
        setNodeRef={setNodeRef}
        isDragging={isDragging}
      />
    </div>
  )
}

export interface ViewSettingsPopoverProps {
  viewSettings: any
  onUpdateViewSettings: (settings: any) => void
}

const ViewSettingsPopover = ({ viewSettings, onUpdateViewSettings }: ViewSettingsPopoverProps) => {
  const [localOrder, setLocalOrder] = useState<TabId[]>(viewSettings.settings.order)
  const sensors = useSensors(useSensor(PointerSensor))
  const tabMap = useMemo(() => Object.fromEntries(tabDefs.map((t) => [t.id, t.label])), [])

  useEffect(() => {
    setLocalOrder(viewSettings.settings.order)
  }, [viewSettings.settings.order])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (active.id !== over?.id) {
        const oldIndex = localOrder.indexOf(active.id as TabId)
        const newIndex = localOrder.indexOf(over?.id as TabId)
        const newOrder = arrayMove(localOrder, oldIndex, newIndex)
        setLocalOrder(newOrder)
        onUpdateViewSettings({ ...viewSettings.settings, order: newOrder })
      }
    },
    [localOrder, onUpdateViewSettings, viewSettings.settings],
  )

  const handleToggle = useCallback(
    (id: TabId) => {
      const newVisible = {
        ...viewSettings.settings.visible,
        [id]: !viewSettings.settings.visible[id],
      }
      onUpdateViewSettings({ ...viewSettings.settings, visible: newVisible })
    },
    [onUpdateViewSettings, viewSettings.settings],
  )

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
        {localOrder.map((id: TabId) => (
          <SortableTabRow
            key={id}
            id={id}
            label={tabMap[id]}
            enabled={viewSettings.settings.visible[id]}
            onToggle={() => handleToggle(id)}
          />
        ))}
      </SortableContext>
    </DndContext>
  )
}

export default ViewSettingsPopover
